import type { Experiment } from "schemas/generated/experiment";

import * as React from "react";

import { useExperimentApi } from "@v1/features/experiments/utils";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { experimentRouter } from "@v2/trpc/routes/experiment";
import { format } from "date-fns";
import { useParams } from "react-router-dom";
import { getExperimentStatus } from "schemas/experiment";

export type SimpleExperiment = Pick<
  Experiment,
  | "id"
  | "name"
  | "createdAt"
  | "activatedAt"
  | "archivedAt"
  | "completedAt"
  | "description"
  | "variations"
> & {
  analyticsLinkUrl: string | null;
  createdAtHuman: string;
  activatedAtHuman: string | null;
  endedAt: Date | null;
  endedAtHuman: string | null;
};

type UseExperimentDetailsResponse = {
  isFetchingExperiments: boolean;
  simpleExperiment: SimpleExperiment | null;
  experiment: Experiment | null;
  workspaceId: string | undefined;
  projectId: string | undefined;
};

export const useExperimentDetails = (): UseExperimentDetailsResponse => {
  const { experimentId } = useParams();
  const projectId = useCurrentProjectId();

  const workspaceId = useCurrentWorkspaceId() ?? undefined;
  const {
    links: { data: links, isLoading: isLoadingLinks },
  } = useExperimentApi();

  const {
    data: { experiment } = {
      experiment: undefined,
    },
    isFetching: isFetchingExperiments,
  } = experimentRouter.findDetail.useQuery(
    {
      experimentId: experimentId ?? "",
      workspaceId,
      projectId,
    },
    {
      enabled:
        Boolean(experimentId) && (Boolean(projectId) || Boolean(workspaceId)),
    },
  );

  if (
    isFetchingExperiments ||
    isLoadingLinks ||
    !links ||
    !experiment ||
    (!workspaceId && !projectId)
  ) {
    return {
      isFetchingExperiments: true,
      simpleExperiment: null,
      experiment: experiment ?? null,
      workspaceId,
      projectId,
    };
  }

  const { id, createdAt, activatedAt, archivedAt, completedAt, name } =
    experiment;
  const link = links?.find((link) => link.experimentId === experimentId);

  const status = getExperimentStatus(experiment);
  const description = experiment.description;
  const createdAtHuman = format(createdAt, "MMMM d, hh:mma");
  const activatedAtHuman =
    activatedAt && status !== "draft"
      ? format(activatedAt, "MMMM d, hh:mma")
      : null;

  const endedAt =
    status === "archived" || status === "complete"
      ? completedAt ?? archivedAt
      : null;

  const endedAtHuman = endedAt ? format(endedAt, "MMMM d, hh:mma") : null;

  const analyticsLinkUrl = link?.url ?? null;
  const variations = experiment.variations;

  return {
    isFetchingExperiments: false,
    simpleExperiment: {
      id,
      name,
      createdAt,
      activatedAt,
      archivedAt,
      completedAt,
      analyticsLinkUrl,
      createdAtHuman,
      activatedAtHuman,
      endedAt,
      endedAtHuman,
      description,
      variations,
    },
    workspaceId,
    projectId,
    experiment,
  };
};
